import React from 'react';
import DOMPurify from 'dompurify';

interface MessageFormatterProps {
  content: string;
  isAssistant: boolean;
  onAction?: (payload: string) => void;
}

type ActionKind = 'itinerary' | 'reserve' | 'site' | 'other';

export default function MessageFormatter({ content, isAssistant, onAction }: MessageFormatterProps) {
  // Accent "majorColor" (doré)
  const MAJOR_GOLD = 'var(--majorColor, var(--major-gold, #F59E0B))';
  const MAJOR_GOLD_SOFT = 'var(--majorColorSoft, rgba(245, 158, 11, 0.14))';

  // Liens : doré côté assistant, blanc côté user
  const linkClass = `underline hover:opacity-80 ${
    isAssistant ? 'chat-major-link' : 'text-white/90'
  }`;

  // ✅ Style uniforme pour tous les boutons ACTION:
  const actionButtonClass = () => {
    const base =
      'inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ' +
      'border transition-colors hover:opacity-90 no-underline';

    if (!isAssistant) {
      return `${base} border-white/30 text-white bg-white/10`;
    }
    return `${base} chat-major-outline text-gray-900 bg-white`;
  };

  const parseActionUrl = (rawUrl: string): { kind: ActionKind; target: string } | null => {
    if (!rawUrl.startsWith('ACTION:')) return null;
    const rest = rawUrl.slice('ACTION:'.length);
    const sep = rest.indexOf('|');
    if (sep === -1) return { kind: 'other', target: rest };
    const kind = (rest.slice(0, sep).trim().toLowerCase() || 'other') as ActionKind;
    const target = rest.slice(sep + 1).trim();
    return { kind: (['itinerary', 'reserve', 'site'].includes(kind) ? kind : 'other') as ActionKind, target };
  };

  const mapUrlFromPayload = (rawUrl: string): string | null => {
    if (!rawUrl.startsWith('MAP:')) return null;

    const payload = rawUrl.slice('MAP:'.length).trim();
    if (!payload) return null;

    // ✅ Supporte MAP: "adresse en clair" ou "adresse déjà encodée"
    let addr = payload;
    try {
      // si ce n'est pas une string encodée valide, decodeURIComponent lèvera -> on garde tel quel
      addr = decodeURIComponent(payload);
    } catch {
      addr = payload;
    }

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
  };

  const formatText = (text: string) => {
    const parts: React.ReactNode[] = [];
    let currentIndex = 0;
    let key = 0;

    const patterns = [
      { regex: /\[([^\]]+)\]\(([^)]+)\)/g, type: 'markdown-link' },
      { regex: /<a\s+(?:[^>]*?\s+)?href="([^"]*)"[^>]*>(.*?)<\/a>/gi, type: 'html-link' },
      { regex: /\*\*(.*?)\*\*/g, type: 'bold' },
      { regex: /(\+33\s?\d(?:[\s.-]?\d{2}){4})/g, type: 'phone' },
      { regex: /(\b0\d(?:[\s.-]?\d{2}){4}\b)/g, type: 'phone' },

      // ✅ NEW: emails cliquables
      { regex: /([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi, type: 'email' },

      { regex: /(https?:\/\/[^\s]+)/g, type: 'link' },
    ];

    const allMatches: Array<{
      index: number;
      length: number;
      match: RegExpExecArray;
      type: string;
    }> = [];

    patterns.forEach(({ regex, type }) => {
      let match: RegExpExecArray | null;
      const localRegex = new RegExp(regex.source, regex.flags);
      while ((match = localRegex.exec(text)) !== null) {
        allMatches.push({
          index: match.index,
          length: match[0].length,
          match,
          type,
        });
      }
    });

    allMatches.sort((a, b) => a.index - b.index);

    const processedRanges = new Set<string>();

    const renderLink = (label: string, rawUrl: string) => {
      // ✅ Action interne (DETAILS:<id>) => bouton (comme ACTION)
      if (rawUrl.startsWith('DETAILS:') && onAction) {
        return (
          <button
            key={key++}
            type="button"
            onClick={() => onAction(rawUrl)}
            className={actionButtonClass()}
          >
            {label}
          </button>
        );
      }

      // ✅ Switch scope action (SWITCH_SCOPE:around:message) => bouton
      if (rawUrl.startsWith('SWITCH_SCOPE:') && onAction) {
        return (
          <button
            key={key++}
            type="button"
            onClick={() => onAction(rawUrl)}
            className={actionButtonClass()}
          >
            {label}
          </button>
        );
      }

      // ✅ Adresse -> Google Maps (MAP:<addr>)
      const maps = mapUrlFromPayload(rawUrl);
      if (maps) {
        return (
          <a
            key={key++}
            href={maps}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
          >
            {label}
          </a>
        );
      }

      // ✅ Boutons d’action (ACTION:<kind>|<url>) => style uniforme
      const action = parseActionUrl(rawUrl);
      if (action) {
        return (
          <a
            key={key++}
            href={action.target}
            target="_blank"
            rel="noopener noreferrer"
            className={actionButtonClass()}
          >
            {label}
          </a>
        );
      }

      // ✅ mailto: ne doit pas ouvrir un nouvel onglet
      const isMailto = rawUrl.startsWith('mailto:');

      // lien externe normal
      return (
        <a
          key={key++}
          href={rawUrl}
          target={isMailto ? undefined : "_blank"}
          rel={isMailto ? undefined : "noopener noreferrer"}
          className={linkClass}
        >
          {label}
        </a>
      );
    };

    allMatches.forEach((item) => {
      if (item.index < currentIndex) return;

      const rangeKey = `${item.index}-${item.index + item.length}`;
      if (processedRanges.has(rangeKey)) return;

      if (item.index > currentIndex) {
        parts.push(text.substring(currentIndex, item.index));
      }

      switch (item.type) {
        case 'markdown-link': {
          const label = item.match[1];
          const url = item.match[2];
          parts.push(renderLink(label, url));
          break;
        }

        case 'html-link': {
          const url = item.match[1];
          const label = item.match[2];
          parts.push(renderLink(label, url));
          break;
        }

        case 'bold': {
          parts.push(
            <strong key={key++} className="font-semibold">
              {item.match[1]}
            </strong>
          );
          break;
        }

        case 'phone': {
          const raw = item.match[1] ?? item.match[0];
          const tel = raw.replace(/[^\d+]/g, '');
          parts.push(
            <a key={key++} href={`tel:${tel}`} className={linkClass}>
              {raw}
            </a>
          );
          break;
        }

        // ✅ NEW: emails cliquables
        case 'email': {
          const email = item.match[1] ?? item.match[0];
          parts.push(
            <a key={key++} href={`mailto:${email}`} className={linkClass}>
              {email}
            </a>
          );
          break;
        }

        case 'link': {
          const url = item.match[1] ?? item.match[0];
          parts.push(
            <a
              key={key++}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
            >
              {url}
            </a>
          );
          break;
        }
      }

      processedRanges.add(rangeKey);
      currentIndex = item.index + item.length;
    });

    if (currentIndex < text.length) {
      parts.push(text.substring(currentIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  const lines = content.replace(/\\n/g, '\n').split('\n');

  const formattedLines = lines.map((line, index) => {
    const trimmed = line.trim();

    // ✅ Ligne spéciale pour titre d'item (plus grand que le texte en gras)
    if (trimmed.startsWith('ITEM_TITLE:')) {
      const title = trimmed.replace(/^ITEM_TITLE:\s*/, '').trim();
      if (!title) return null;

      return (
        <div key={index} className="font-bold text-lg mb-1">
          {formatText(title)}
        </div>
      );
    }

    // ✅ Ligne spéciale HTML (pour descriptions avec formatage)
    if (trimmed.startsWith('HTML_CONTENT:')) {
      const htmlContent = trimmed.replace(/^HTML_CONTENT:\s*/, '').trim();
      if (!htmlContent) return null;

      return (
        <div
          key={index}
          className="my-2 leading-relaxed html-description"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlContent) }}
        />
      );
    }

    // ✅ Ligne spéciale image (Détails uniquement)
    if (trimmed.startsWith('IMAGE_URL:')) {
      const url = trimmed.replace(/^IMAGE_URL:\s*/, '').trim();
      if (!url) return null;

      return (
        <div key={index} className="mt-2 mb-2">
          <img
            src={url}
            alt=""
            loading="lazy"
            className="w-full max-w-full rounded-lg border border-black/5"
            onError={(e) => {
              // si l'image ne charge pas, on masque le bloc sans casser le message
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      );
    }

    // ✅ Ligne spéciale pour rangée de boutons (sans puce)
    if (trimmed.startsWith('ACTIONS_ROW:')) {
      const row = trimmed.replace(/^ACTIONS_ROW:\s*/, '');
      return (
        <div key={index} className="mt-2 flex flex-wrap gap-2">
          {formatText(row)}
        </div>
      );
    }

    const trimmedStart = line.trimStart();

    const isBullet =
      trimmedStart.startsWith('- ') ||
      trimmedStart.startsWith('* ') ||
      trimmedStart.startsWith('• ');

    if (isBullet) {
      const bulletText = trimmedStart.replace(/^(-|\*|•)\s+/, '');

      return (
        <div key={index} className="flex items-start gap-2 my-0.5 leading-snug">
          <span className="pt-[2px] leading-none">•</span>
          <span className="flex-1">{formatText(bulletText)}</span>
        </div>
      );
    }

    return (
      <React.Fragment key={index}>
        {formatText(line)}
        {index < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });

  return (
    <>
      <style>{`
        .chat-major-link {
          color: ${MAJOR_GOLD};
        }
        .chat-major-link:hover {
          opacity: 0.85;
        }
        .chat-major-outline {
          border-color: ${MAJOR_GOLD};
        }
        .chat-major-outline:hover {
          background: ${MAJOR_GOLD_SOFT};
        }
      `}</style>
      <div className="text-sm whitespace-pre-wrap">{formattedLines}</div>
    </>
  );
}
